import {
  For,
  Show,
  createEffect,
  createSignal,
  type JSX,
} from "solid-js";
import { useNavigate } from "@solidjs/router";
import { api } from "../api/client";
import { t } from "../i18n";
import { createCachedResource } from "../lib/cache";
import { todayISO } from "../lib/repeatWorkout";
import BottomSheet from "./BottomSheet";

const HAS_TEMPLATES_KEY = "alex-tracker:has-templates";

/** session cache: true/false, or null if never checked this session */
function readHasTemplatesCache(): boolean | null {
  try {
    const v = sessionStorage.getItem(HAS_TEMPLATES_KEY);
    if (v === "1") return true;
    if (v === "0") return false;
    return null;
  } catch {
    return null;
  }
}

function writeHasTemplatesCache(has: boolean) {
  try {
    sessionStorage.setItem(HAS_TEMPLATES_KEY, has ? "1" : "0");
  } catch {
    /* ignore */
  }
}

interface Props {
  open: boolean;
  onClose: () => void;
}

type View = "menu" | "templates";

export default function CreateWorkoutSheet(props: Props) {
  const navigate = useNavigate();
  const [view, setView] = createSignal<View>("menu");
  // Default off; session cache can optimistically enable before the network returns
  const [templatesAvailable, setTemplatesAvailable] = createSignal(
    readHasTemplatesCache() === true,
  );
  const [templates] = createCachedResource(() => props.open, {
    key: () => "templates",
    fetch: () => api.listTemplates(),
  });

  createEffect(() => {
    if (!props.open) setView("menu");
  });

  // Sync button state + session cache when the list resolves
  createEffect(() => {
    if (templates.loading || templates.error) return;
    const list = templates();
    if (list === undefined) return;
    const has = list.length > 0;
    writeHasTemplatesCache(has);
    setTemplatesAvailable(has);
    // If cache was wrong and list is empty, leave the nested view
    if (!has && view() === "templates") setView("menu");
  });

  function close() {
    setView("menu");
    props.onClose();
  }

  function go(path: string) {
    close();
    navigate(path);
  }

  const canUseTemplates = () => templatesAvailable();

  return (
    <BottomSheet
      open={props.open}
      onClose={close}
      title={view() === "templates" ? t("FROM_TEMPLATE") : t("CREATE_WORKOUT")}
      ariaLabel={t("CREATE_WORKOUT")}
    >
      <Show when={view() === "menu"}>
        <div class="tg-section" role="menu">
          <SheetAction
            icon={<PlayIcon />}
            label={t("START_WORKOUT_NOW")}
            hint={t("START_WORKOUT_NOW_HINT")}
            accent
            onClick={() => go("/workouts/active?new=1")}
          />
          <SheetAction
            icon={<CheckIcon />}
            label={t("LOG_COMPLETED_SHORT")}
            hint={t("LOG_COMPLETED_HINT")}
            onClick={() => go(`/workouts/new/completed?date=${todayISO()}`)}
          />
          <SheetAction
            icon={<CalendarIcon />}
            label={t("PLAN_WORKOUT_SHORT")}
            hint={t("PLAN_WORKOUT_HINT")}
            onClick={() => go(`/workouts/new/planned?date=${todayISO()}`)}
          />
          <SheetAction
            icon={<TemplateIcon />}
            label={t("FROM_TEMPLATE_SHORT")}
            hint={
              canUseTemplates()
                ? t("FROM_TEMPLATE_HINT")
                : t("NO_TEMPLATES")
            }
            disabled={!canUseTemplates()}
            onClick={() => {
              if (!canUseTemplates()) return;
              setView("templates");
            }}
          />
        </div>
        <div class="tg-section mt-2">
          <button type="button" class="tg-cell" onClick={close}>
            <span class="tg-cell-body">
              <span
                class="tg-cell-title"
                style={{
                  "text-align": "center",
                  color: "var(--link)",
                  width: "100%",
                }}
              >
                {t("CANCEL")}
              </span>
            </span>
          </button>
        </div>
      </Show>

      <Show when={view() === "templates"}>
        <div class="space-y-2">
          <button
            type="button"
            class="btn-ghost w-full justify-start px-2 text-xs text-ink-muted"
            onClick={() => setView("menu")}
          >
            ‹ {t("BACK")}
          </button>
          <Show when={templates.loading}>
            <p class="py-6 text-center text-sm text-ink-muted">{t("LOADING")}</p>
          </Show>
          <div class="tg-section" role="menu">
            <For each={templates()}>
              {(tpl) => (
                <SheetAction
                  icon={<TemplateIcon />}
                  label={tpl.name}
                  hint={`${tpl.exercises.length} ${t("EXERCISES").toLowerCase()}`}
                  onClick={() =>
                    go(
                      `/workouts/active?fromTemplate=${encodeURIComponent(tpl.id)}`,
                    )
                  }
                />
              )}
            </For>
          </div>
        </div>
      </Show>
    </BottomSheet>
  );
}

function SheetAction(props: {
  icon: JSX.Element;
  label: string;
  hint?: string;
  accent?: boolean;
  disabled?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      role="menuitem"
      disabled={props.disabled}
      class={`tg-cell has-leading ${props.disabled ? "cursor-not-allowed opacity-40" : ""}`}
      onClick={() => {
        if (!props.disabled) props.onClick();
      }}
    >
      <span class="tg-cell-leading">{props.icon}</span>
      <span class="tg-cell-body">
        <span
          class={`tg-cell-title ${props.accent ? "text-[var(--link)]" : ""}`}
        >
          {props.label}
        </span>
        <Show when={props.hint}>
          <span class="tg-cell-subtitle">{props.hint}</span>
        </Show>
      </span>
      <Show when={!props.disabled}>
        <svg class="tg-chevron" viewBox="0 0 9 16" aria-hidden="true">
          <path
            d="M1.5 1.5L7.5 8L1.5 14.5"
            fill="none"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
      </Show>
    </button>
  );
}

function PlayIcon() {
  return (
    <svg class="h-5 w-5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M8 5.14v13.72a1 1 0 001.5.86l11-6.86a1 1 0 000-1.72l-11-6.86a1 1 0 00-1.5.86z" />
    </svg>
  );
}

function CheckIcon() {
  return (
    <svg
      class="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="2"
      aria-hidden="true"
    >
      <path stroke-linecap="round" stroke-linejoin="round" d="M5 13l4 4L19 7" />
    </svg>
  );
}

function CalendarIcon() {
  return (
    <svg
      class="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="1.8"
      aria-hidden="true"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
      />
    </svg>
  );
}

function TemplateIcon() {
  return (
    <svg
      class="h-5 w-5"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      stroke-width="1.8"
      aria-hidden="true"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        d="M4 6h16M4 10h16M4 14h10M4 18h10"
      />
    </svg>
  );
}
